import {gql} from '@apollo/client';
import produce from 'immer';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {FirstLookupStepFormProps} from 'lookup-config/first-lookup-step/FirstLookupStepFormSection';
import LookupConfigModal, {LookupConfigModalProps} from 'lookup-config/LookupConfigModal';
import {
  LookupStepSpec,
  LookupConfigSpec,
  LookupStepType,
  NOTEBOOK_FINAL_LOOKUP_STEP_TYPES,
  NOTEBOOK_INTERMEDIATE_LOOKUP_STEP_TYPES,
  NotebookColumnItemType,
  LookupSortType,
} from 'lookup-config/LookupConfigModalConstants';
import * as LookupColumnsDAO from 'notebook/structured-tables/lookup-columns/LookupColumnsDAO';
import {
  DedicatedLookupStructuredTableColumn,
  LookupConfig,
} from 'notebook/structured-tables/StructuredTableConstants';
import {ColumnConstraint, InsertableColumnInfo, TableColumnType} from 'notebook/tables/TableConstants';
import notifications from 'notifications';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';

import {getColumnLabel} from '../columns/ColumnDefinitions';
import toColumnDefinition from '../columns/toColumnDefinition';
import {makeStructuredTableColumn} from '../util/StructuredTableColumnUtil';
import {NotebookLookupConfigModalQuery} from './__generated/NotebookLookupConfigModalQuery';
import NotebookFirstLookupStepForm from './NotebookFirstLookupStepForm';
import {
  getDefaultLookupStepForInputColumn,
  getInitialInputColumnProvenanceUuid,
} from './NotebookLookupColumnUtils';

const CREATE_LUC_MODAL_SUBMIT_BUTTON_TEXT = 'Insert';
const EDIT_LUC_MODAL_SUBMIT_BUTTON_TEXT = 'Update';

export interface NotebookColumnInfo {
  name: string;
  provenanceUuid: string;
  itemType: NotebookColumnItemType;
  // Additional metadata to be smarter about what following lookups to show when we don't know what specific
  // type a storable lookup is, but know it must be a parent storable
  storableLookupMustBeParent?: boolean;
  schemaId?: string | null;
}

export function createLookupColumnFromConfig({
  lookupConfig,
  finalStepName,
  notebookInputColumnProvenanceUuid,
  unitApiIdentifier,
  lookupSortType,
}: {
  lookupConfig: LookupConfig;
  finalStepName: string;
  notebookInputColumnProvenanceUuid?: string | null;
  unitApiIdentifier?: string | null;
  lookupSortType?: LookupSortType;
}): InsertableColumnInfo {
  assert(
    notebookInputColumnProvenanceUuid != null,
    'Lookup column was configured without any column to use as input'
  );

  const structuredTableColumn: DedicatedLookupStructuredTableColumn = {
    ...makeStructuredTableColumn({
      columnType: TableColumnType.DEDICATED_LOOKUP,
      name: finalStepName,
    }),
    columnType: TableColumnType.DEDICATED_LOOKUP, // needed to satisfy TypeScript
    frontEndMetadata: undefined,
    lookupConfig,
    inputColumnProvenanceUuid: notebookInputColumnProvenanceUuid,
    unitApiIdentifier,
    lookupSortType,
  };
  const columnDefinition = toColumnDefinition({structuredTableColumn, schemas: []});
  return {
    columnLabels: getColumnLabel(columnDefinition),
    columnConstraints: {
      structuredTableColumn,
      columnDefinition,
    },
    widths: structuredTableColumn.width,
  };
}

async function _createLookupColumnInfoFromModalSubmitResult(
  modalSubmitResult: Parameters<LookupConfigModalProps<ExtraState>['onRequestSubmit']>[0] | null
): Promise<InsertableColumnInfo | null> {
  if (modalSubmitResult == null) {
    return null;
  }

  const {
    lookupConfig: lookupConfigSpec,
    finalStepName,
    finalStepSchemaField,
    extraState,
    finalSortType,
  } = modalSubmitResult;
  const {lookupConfig} = await withLoadingScreen(
    LookupColumnsDAO.createNotebookLookupConfig(lookupConfigSpec)
  );
  return createLookupColumnFromConfig({
    lookupConfig,
    finalStepName,
    notebookInputColumnProvenanceUuid: extraState.notebookInputColumnProvenanceUuid,
    unitApiIdentifier: finalStepSchemaField?.unitApiIdentifier,
    lookupSortType: finalSortType,
  });
}

interface ExtraState {
  notebookInputColumnProvenanceUuid: string | null;
}

export function renderFirstLookupStepFormForNotebook(
  firstLookupStepFormProps: FirstLookupStepFormProps<ExtraState>,
  notebookColumnInfos: Array<NotebookColumnInfo>
): JSX.Element {
  return (
    <NotebookFirstLookupStepForm
      disabled={firstLookupStepFormProps.disabled}
      notebookColumnInfos={notebookColumnInfos}
      notebookInputColumnProvenanceUuid={
        firstLookupStepFormProps.extraState.notebookInputColumnProvenanceUuid
      }
      setNotebookInputColumnProvenanceUuid={(provenanceUuid: string | null) => {
        firstLookupStepFormProps.setExtraState({notebookInputColumnProvenanceUuid: provenanceUuid});
      }}
      lookupStepGroup={firstLookupStepFormProps.lookupStepGroup}
      updateLookupStepGroup={firstLookupStepFormProps.handleSetLookupStepGroup}
    />
  );
}

export function getInitialLookupConfigForCreateModal(
  notebookColumnInfos: Array<NotebookColumnInfo>
): LookupConfigSpec | null {
  const initialInputColumnProvenanceUuid = getInitialInputColumnProvenanceUuid(notebookColumnInfos);
  const {lookupStep: initialLookupStep} = getDefaultLookupStepForInputColumn(
    notebookColumnInfos,
    initialInputColumnProvenanceUuid
  );
  return initialLookupStep
    ? {
        isMulti: true,
        lookupSteps: [initialLookupStep],
      }
    : null;
}

export const NOTEBOOK_LOOKUP_CONFIG_MODAL_QUERY = gql`
  query NotebookLookupConfigModalQuery @skipAudit @engComponent(name: NOTEBOOK) {
    ...AllAssaySchemasGraphQL
    ...AllStorableSchemasGraphQL
    ...AllCompleteTagSchemasGraphQL
    ...AllSchemaInterfacesGraphQL
  }
  ${getFragmentWrapper('AllAssaySchemas').fragment}
  ${getFragmentWrapper('AllStorableSchemas').fragment}
  ${getFragmentWrapper('AllCompleteTagSchemas').fragment}
  ${getFragmentWrapper('AllSchemaInterfaces').fragment}
`;

/**
 * Parse the table and run the LookupConfigModal, and return whatever lookup column the user configures.
 *
 * To be used when inserting a new lookup column to a structured table.
 */
export async function runCreateLookupColumnConfigModal(
  notebookColumnInfos: Array<NotebookColumnInfo>
): Promise<{
  lookupColumnToInsert: InsertableColumnInfo;
} | null> {
  if (notebookColumnInfos.length === 0) {
    // This red toast may be jarring, but it is better than the alternatives:
    //
    // - Showing the config modal with an error message, which would require more time and more clicks from the user
    // - Disabling the "Insert lookup column" menu options without explanation, which would be very confusing
    // - Disabling the "Insert lookup column" menu options and changing the option names include an explanation,
    //   because the explanation would need to be ridiculously long
    //
    // Ideally we could disable the menu options and show a tooltip, but that's hard to do with our current context menu implementation
    notifications.error(
      'This table does not support lookup columns. ' +
        'To support lookup columns, a table must have at least one column that contains either entities or inventory items.',
      {
        duration: 10,
      }
    );
    return null;
  }
  // Show loading screen and fetch everything
  // This might take a little time the first time the modal is opened, but if the modal is opened again all the data should be in cache
  await withLoadingScreen(
    doQuerySafe<NotebookLookupConfigModalQuery, {}>({query: NOTEBOOK_LOOKUP_CONFIG_MODAL_QUERY})
  );
  const initialLookupConfig = getInitialLookupConfigForCreateModal(notebookColumnInfos);

  const modalSubmitResult = await runModal<LookupConfigModalProps<ExtraState>>(LookupConfigModal, {
    renderFirstLookupStepForm: (props: FirstLookupStepFormProps<ExtraState>) =>
      renderFirstLookupStepFormForNotebook(props, notebookColumnInfos),
    initialExtraState: {
      notebookInputColumnProvenanceUuid: getInitialInputColumnProvenanceUuid(notebookColumnInfos),
    },
    baseSchema: null,
    initialLookupConfig,
    firstLookupStepTypes: [LookupStepType.NOTEBOOK_COLUMN_TYPECHECK],
    intermediateLookupStepTypes: NOTEBOOK_INTERMEDIATE_LOOKUP_STEP_TYPES,
    finalLookupStepTypes: NOTEBOOK_FINAL_LOOKUP_STEP_TYPES,
    submitButtonText: CREATE_LUC_MODAL_SUBMIT_BUTTON_TEXT,
    allowMissingParentSchema: true,
  });
  if (modalSubmitResult == null) {
    return null;
  }

  const {
    lookupConfig: lookupConfigSpec,
    finalStepName,
    finalStepSchemaField,
    extraState,
    finalSortType,
  } = modalSubmitResult;
  const {lookupConfig} = await withLoadingScreen(
    LookupColumnsDAO.createNotebookLookupConfig(lookupConfigSpec)
  );
  const lookupStepsTypes = lookupConfigSpec.lookupSteps.map((lookupStep, idx) => lookupStep.type);
  const firstLookupStep = lookupConfigSpec.lookupSteps[0];
  assert(
    firstLookupStep.type === LookupStepType.NOTEBOOK_COLUMN_TYPECHECK,
    'First lookup step should be notebook column typecheck.'
  );
  analytics.deprecatedTrackEvent(ELN_ANALYTICS_ACTIONS.CREATE_NOTEBOOK_LOOKUP_CONFIG, {
    lookupConfigId: lookupConfig.id,
    lookupStepsTypes, // Deprecated - add fields to LookupStepAnalyticsMetadata instead of adding more arrays here.
    lookupStepsMetadata: lookupConfigSpec.lookupSteps.map((lss) => getLookupStepAnalyticsMetadata(lss)),
    lookupInputColumnType: firstLookupStep.notebookColumnItemType,
    lookupInputColumnEntitySchema: firstLookupStep.notebookColumnEntitySchema,
    lookupInputColumnStorableSchema: firstLookupStep.notebookColumnStorableSchema,
  });
  return {
    lookupColumnToInsert: createLookupColumnFromConfig({
      lookupConfig,
      finalStepName,
      lookupSortType: finalSortType,
      unitApiIdentifier: finalStepSchemaField?.unitApiIdentifier,
      notebookInputColumnProvenanceUuid: extraState.notebookInputColumnProvenanceUuid,
    }),
  };
}

/**
 * Analytics metadata for a single lookup step.
 *
 * Avoid any breaking changes, as the data team's SQL queries rely on this shape.
 */
interface LookupStepAnalyticsMetadata {
  lookupStepType: LookupStepType;

  // Defined for SCHEMA_FIELD lookup steps.
  schemaField?: string;
}

/**
 * Get a metadata object for the given lookup step for analytics purposes.
 */
function getLookupStepAnalyticsMetadata(lookupStepSpec: LookupStepSpec): LookupStepAnalyticsMetadata {
  const metadata: LookupStepAnalyticsMetadata = {lookupStepType: lookupStepSpec.type};

  if (lookupStepSpec.type === LookupStepType.SCHEMA_FIELD) {
    // The schema field can be null if the field has been deleted, but we don't expect that here, since we
    // created the step right before capturing this metadata.
    metadata.schemaField = lookupStepSpec.schemaField!;
  }

  return metadata;
}

export async function runEditLookupConfigModal(
  notebookColumnInfos: Array<NotebookColumnInfo>,
  originalLookupColumnConstraint: ColumnConstraint,
  initialInputColumnInfo?: NotebookColumnInfo,
  initialLookupConfig?: LookupConfigSpec
): Promise<InsertableColumnInfo | null> {
  // Show loading screen and fetch everything
  // This might take a little time the first time the modal is opened, but if the modal is opened again all the data should be in cache
  await withLoadingScreen(
    doQuerySafe<NotebookLookupConfigModalQuery, {}>({query: NOTEBOOK_LOOKUP_CONFIG_MODAL_QUERY})
  );
  const selectedLookupIsParent = initialInputColumnInfo?.storableLookupMustBeParent;
  const columnDefinition = originalLookupColumnConstraint.columnDefinition;
  assert(
    columnDefinition != null && columnDefinition.type === TableColumnType.DEDICATED_LOOKUP,
    'Column to edit is not a lookup column.'
  );

  const modalSubmitResult = await runModal<LookupConfigModalProps<ExtraState>>(LookupConfigModal, {
    renderFirstLookupStepForm: (props: FirstLookupStepFormProps<ExtraState>) =>
      renderFirstLookupStepFormForNotebook(props, notebookColumnInfos),
    initialExtraState: {
      notebookInputColumnProvenanceUuid: getInitialInputColumnProvenanceUuid(
        notebookColumnInfos,
        initialInputColumnInfo
      ),
    },
    baseSchema: null,
    initialLookupConfig,
    firstLookupStepTypes: [LookupStepType.NOTEBOOK_COLUMN_TYPECHECK],
    intermediateLookupStepTypes: NOTEBOOK_INTERMEDIATE_LOOKUP_STEP_TYPES,
    finalLookupStepTypes: NOTEBOOK_FINAL_LOOKUP_STEP_TYPES,
    submitButtonText: EDIT_LUC_MODAL_SUBMIT_BUTTON_TEXT,
    allowMissingParentSchema: true,
    context: {
      storableLookupMustBeParent: selectedLookupIsParent,
    },
    finalSortType: columnDefinition.lookupSortType,
  });

  // Create new insertable column info based on selections in LookupConfigModal
  // Note that we will always create a new lookup config even if the user submits an identical config to the
  // one they were initially editing.
  const brandNewLookupColumnInfo = await _createLookupColumnInfoFromModalSubmitResult(modalSubmitResult);
  if (brandNewLookupColumnInfo == null) {
    return null;
  }

  // Modify the "new" insertable info to keep the same provenance UUID and cid as the existing lookup column.
  const updatedOriginalLookupColumnInfo = produce(brandNewLookupColumnInfo, (draftLookupColumnInfo) => {
    const {structuredTableColumn} = draftLookupColumnInfo.columnConstraints!;
    assert(
      structuredTableColumn != null,
      'InsertableColumnInfo was created from lookup config without a column constraint / structured table column.'
    );
    structuredTableColumn.cid = originalLookupColumnConstraint.structuredTableColumn!.cid;
    structuredTableColumn.provenanceUuid =
      originalLookupColumnConstraint.structuredTableColumn!.provenanceUuid;
    return draftLookupColumnInfo;
  });
  return updatedOriginalLookupColumnInfo;
}
